// src/pages/PropertyManagementPage.jsx
//
// R31.7d (post-Sarah R34d 8/10 YELLOW closeout): dedicated landing page
// for property-management operators. Sarah's Sandhill / Cedar Ridge
// walks (R32 / R33 / R34a-d) caught the gap repeatedly — a PM operator
// landing on /real-estate gets brokerage vocabulary (deals, listings,
// commissions, MLS) that doesn't fit a "manage doors" business. The
// engine has been PM-aware since R31.3 (PM cluster taxonomy, AppFolio +
// Buildium + RentSpree recognition, FCRA fence with TurboTenant /
// TransUnion SmartMove safe alternatives) — this page is the funnel
// front-door that finally matches the engine's vocabulary.
//
// All audit submissions from this page route to industry=real_estate
// AND stamp sub_vertical=property_management on the query string so
// the engine's chip-resolver + cluster taxonomy + FCRA fence see the
// right context from the first hop.
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  ArrowRight, CheckCircle, DollarSign, Clock,
  Building2, Wrench, FileText, Calendar, ShieldCheck, Users,
} from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "../hooks/useLanguage";
import { captureAttribution } from "../utils/attribution";

const PAIN_ICONS = [ShieldCheck, Wrench, Calendar, FileText, Users, Building2];
const PAIN_COLORS = [
  { color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
  { color: "text-orange-400",  bg: "bg-orange-500/10",  border: "border-orange-500/20" },
  { color: "text-blue-400",    bg: "bg-blue-500/10",    border: "border-blue-500/20" },
  { color: "text-yellow-400",  bg: "bg-yellow-500/10",  border: "border-yellow-500/20" },
  { color: "text-purple-400",  bg: "bg-purple-500/10",  border: "border-purple-500/20" },
  { color: "text-red-400",     bg: "bg-red-500/10",     border: "border-red-500/20" },
];
const TOOL_META = [
  { score: 93, color: "bg-emerald-500" },
  { score: 89, color: "bg-blue-500" },
  { score: 86, color: "bg-purple-500" },
  { score: 82, color: "bg-cyan-500" },
];

const CONTENT = {
  en: {
    title: "AI Blueprint for Property Managers — Cut Tenant Screening from 5 Days to 2 Hours",
    metaDesc: "Personalized AI implementation blueprint for property managers. FCRA-compliant tenant screening, automated maintenance dispatch, lease-renewal nurture, and vendor coordination — built from real PM tool data.",
    heroBadge: "Built for property managers",
    heroTitlePart1: "Which AI Tools Will Help You",
    heroTitlePart2: "Manage Doors Without Drowning?",
    heroSubtitlePre: "Answer 8 questions about your portfolio. Get a personalized blueprint with the",
    heroSubtitleBold: "exact tools, FCRA-safe screening stack, and ROI projections",
    heroSubtitlePost: "— built from",
    heroSubtitleEnd: "vetted AI tools and 2026 property-management benchmarks.",
    heroBullets: [
      "Cut tenant screening from 5 days to 2 hours — FCRA-compliant from day one",
      "Auto-dispatch maintenance work orders to the right vendor with SLA tracking",
      "Catch lease renewals 60+ days out, not when the tenant gives notice",
      "Owner reports in 10 minutes, not 4 hours — branded and personalized",
    ],
    heroCta: "Get My PM AI Blueprint",
    heroCtaFine: "One-time — No subscription — Ready in minutes",
    painEyebrow: "Sound familiar?",
    painHeading: "The 6 Operational Leaks Costing PMs Their Margin",
    painSub: "AI can fix all of these — but the wrong tool creates real liability (FCRA, fair-housing). The blueprint tells you which ones are safe, which ones aren't, and what to roll out first.",
    painPoints: [
      { title: "Tenant screening takes 5+ days per applicant", desc: "Paper applications + faxed credit pulls = a week before you can say yes or no, and your best applicant signed elsewhere. FCRA-compliant tenant-screening platforms get the same decision in under 2 hours, with permissible-purpose audit trail and adverse-action letters built in.", stat: "5 days → 2 hours" },
      { title: "Maintenance requests scatter across texts and voicemails", desc: "Tenants text the on-call line, email the office, and DM Instagram. Vendors get double-booked or miss the ticket entirely. AI maintenance dispatch tools create one ticket queue, route to the right vendor, and surface SLA breaches before the tenant calls you.", stat: "12 vendor calls → 1 ticket" },
      { title: "Lease renewals go reactive — tenants give notice first", desc: "By the time you start the renewal conversation, the tenant has already toured a competing unit. AI lease-renewal nurture flags expirations 60-90 days out and runs personalized retention sequences — anniversary, market update, lease offer.", stat: "60-day proactive vs reactive" },
      { title: "Vendor coordination is manual and missed handoffs cost units", desc: "Photographer, inspector, locksmith, cleaner, repair tech — every turnover is 12 calls and 30 texts. One late vendor pushes your make-ready by a week and that's $1,500–3,000 of lost rent per unit.", stat: "$1,500–3,000/unit at risk" },
      { title: "Owner reports eat 4+ hours per property per month", desc: "Owners want NOI, vacancy days, maintenance spend, and rent collection — and they want it personalized to their unit, not a generic export. AI owner-reporting tools generate branded monthly reports in 10 minutes from your AppFolio/Buildium data.", stat: "4 hrs → 10 min/property" },
      { title: "Rent collection chases eat the back-office", desc: "Late-payment chases bounce between calls, texts, and email. Inconsistent follow-up = late rent, escalated turnovers, and small claims court. AI rent-collection tools standardize the cadence and integrate with your PM platform's payment rail.", stat: "30%+ faster collection" },
    ],
    toolEyebrow: "Tool stack preview",
    toolHeading: "FCRA-Safe Tools Your Blueprint Could Recommend",
    toolSubPre: "These are examples. Your blueprint picks from",
    toolSubPost: "+ tools based on your portfolio size, jurisdictions, and budget.",
    toolStack: [
      { name: "RentSpree", category: "FCRA-permissible Tenant Screening", price: "Free for landlords (applicant pays)", why: "FCRA-permissible end-to-end tenant screening — credit (TransUnion), eviction history, criminal background, income verification, lease e-sign. Used by 1M+ landlords. The piece of the application stack you legally MUST run through a credentialed CRA. Free for landlords; applicants pay the report fee. Built-in adverse-action notice templates keep you FCRA §615 compliant." },
      { name: "AppFolio + AI Add-ons", category: "PM Platform with Native AI", price: "From ~$1.40/unit/mo", why: "If you're not on AppFolio yet and you have 50+ doors, this is usually the foundation. Native maintenance ticketing, online rent collection, owner portals, and an AI assistant for tenant communication. The blueprint tells you which AppFolio modules to enable first and which third-party tools layer on top." },
      { name: "TurboTenant", category: "All-in-one Independent Landlord PM", price: "Free core tier · Premium from $13/mo", why: "Free FCRA-permissible screening + rent collection + applications + lease e-sign + maintenance ticketing. Built for solo and small-portfolio PMs (under 50 doors) who need an all-in-one without the AppFolio price point. 650K+ landlords. Adverse-action templates included." },
      { name: "TransUnion SmartMove", category: "First-party Bureau Screening", price: "From $25 per screening", why: "First-party TransUnion screening — the credit bureau itself, not a reseller. FCRA-permissible by construction. Use when you want the most defensible audit trail or as a second-opinion check on edge applicants from RentSpree/TurboTenant. Pay-per-screening, no subscription." },
    ],
    matchLabel: "match",
    toolFootnotePre: "Your blueprint selects 5—8 tools from a database of",
    toolFootnotePost: "+, ranked for your specific portfolio.",
    getEyebrow: "What you get",
    getHeadingPre: "Your $",
    getHeadingPost: " Property Management AI Blueprint Includes",
    whatYouGet: [
      { title: "Your exact PM AI tool stack — FCRA-aware", desc: "5—8 tools matched to your unit count, jurisdictions, and biggest bottleneck. Tenant-screening tools are filtered to FCRA-permissible only — no AI receptionist or generic CRM masquerading as a screening service." },
      { title: "12-week implementation roadmap", desc: "Phase 1 fixes your highest-cost leak — usually tenant screening time-to-decision or maintenance ticket chaos. Week-by-week, paced for an operator with 2-4 hours of admin time per week." },
      { title: "Custom prompt library for property managers", desc: "15-30 prompts tuned for PM workflows: lease-renewal outreach, maintenance status updates, owner monthly reports, late-rent follow-up, vendor coordination, fair-housing-safe applicant communication." },
      { title: "ROI projections for your portfolio", desc: "Expected savings from faster turnover, lower vacancy days, retained tenants, and reclaimed back-office hours — modeled on your unit count and rent roll, not a generic template." },
      { title: "Compliance + risk guide", desc: "What FCRA permissible-purpose actually means, when adverse-action letters fire, fair-housing safeguards in AI applicant screening, and which tools are flagged as NOT permissible for the regulated steps." },
      { title: "First 30 days checklist", desc: "Day-by-day tasks to get your first FCRA-compliant screening end-to-end, your first AI-routed maintenance ticket, and your first 60-day lease-renewal nurture live." },
    ],
    testimonialLaunch: "We launched recently — real PM operator results coming soon.",
    testimonialGuarantee: "60-day money-back guarantee if it doesn't deliver value.",
    bottomHeading1: "Stop Drowning in Manual Operations.",
    bottomHeading2: "Start Running a Compliant, Automated Portfolio.",
    bottomSub: "Answer 8 questions about your portfolio. Get a blueprint with your exact tool stack, FCRA-safe screening pipeline, and week-by-week roadmap. Ready in minutes.",
    bottomCta: "Get My PM AI Blueprint",
    bottomFineReady: "Ready in minutes",
    bottomFineOneTime: "One-time $",
    bottomFineNoSub: "No subscription",
    preview: {
      eyebrow: "AI Implementation Blueprint",
      kicker: "Property Management — Operators",
      reportReady: "✓ Report Ready",
      roiLabel: "Year 1 ROI",
      hoursSavedLabel: "Saved/week",
      toolsLabel: "Recommended",
      hrsUnit: "hrs",
      toolsUnit: "tools",
      topToolsEyebrow: "Top Tools For Your Portfolio",
      toolBadges: ["FCRA Screening — Free", "PM Platform — $1.40/door", "Independent — $13/mo"],
      matchShort: "match",
      roadmapEyebrow: "12-Week Roadmap",
      phaseLabels: ["Phase 1\nTenant Screening", "Phase 2\nMaintenance + Renewals", "Phase 3\nOwner Reporting"],
      floatingBadge1: "✨ FCRA-aware",
      floatingBadge2: "PM-optimized",
    },
    faqEyebrow: "Frequently asked",
    faqHeading: "Before you build your blueprint",
    faq: [
      { q: "I'm a solo landlord / small operator (under 50 doors) / mid-market (50-500) / institutional. Does this still apply?", a: "Yes. The blueprint asks about your unit count, your jurisdictions (some states have strict tenant-screening rules), your existing PM platform, and your biggest bottleneck — then ranks tools to fit. Solo landlords typically get TurboTenant + a screening service; mid-market PMs get AppFolio/Buildium + RentSpree + maintenance dispatch AI; institutional gets enterprise modules + custom integrations." },
      { q: "I'm worried about FCRA / fair-housing compliance with AI tools.", a: "That's the right concern, and it's why we built this. The blueprint flags every recommendation against FCRA permissible-purpose. Tenant-screening services are filtered to credentialed CRAs only (TransUnion SmartMove, RentSpree, TurboTenant, AppFolio's native screening). Receptionist + CRM tools are explicitly blocked from being recommended for the regulated steps. We surface adverse-action notice templates and the §615 timing rules in the compliance section." },
      { q: "How long does the blueprint take to implement?", a: "It's a 12-week roadmap built for PMs with 2—4 hours a week to invest. Phase 1 targets your highest-cost leak first — usually tenant-screening time-to-decision or maintenance ticket chaos — and most operators see measurable results (faster screening, fewer vendor double-bookings) in 2—3 weeks before Phase 2." },
      { q: "I already use AppFolio / Buildium / Yardi. Does this conflict?", a: "No. The blueprint specifically detects your PM platform from your stack input and recommends tools that integrate with what you already run. It will NOT recommend a competing CRM on top of AppFolio (we built explicit suppression for that). It will recommend layered tools — screening, maintenance dispatch, owner reporting, lease nurture — that play nicely with your existing platform." },
      { q: "Is my tenant and owner data safe?", a: "Every tool recommendation includes a data-handling note — where tenant data is stored, whether it's used to train models, and whether the vendor shares data with third parties. Tools that train on tenant PII by default are flagged, not quietly recommended. FCRA-regulated tools are pre-vetted for SOC2 + GLBA compliance." },
      { q: "What if the blueprint doesn't fit my portfolio?", a: "60-day money-back guarantee, no questions asked. Email us within 60 days and we refund in full. You keep the blueprint. We'd rather you leave happy than keep $149 on a report you couldn't use." },
    ],
  },
};

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

const PMBlueprintPreview = ({ c }) => (
  <div className="relative select-none">
    <div className="absolute -inset-4 bg-gradient-to-br from-emerald-600/20 via-teal-600/20 to-cyan-600/20 rounded-3xl blur-2xl" />
    <div className="relative rounded-2xl overflow-hidden bg-slate-800/90 border border-white/10 shadow-2xl backdrop-blur-sm">
      <div className="bg-slate-900 px-5 py-3.5 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[10px] text-emerald-400 font-semibold tracking-widest uppercase mb-0.5">{c.preview.eyebrow}</div>
          <div className="text-sm font-bold text-white">{c.preview.kicker}</div>
        </div>
        <span className="text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2.5 py-1 rounded-full">{c.preview.reportReady}</span>
      </div>
      <div className="grid grid-cols-3 border-b border-white/10">
        {[["$18–94K", c.preview.roiLabel], [`12 ${c.preview.hrsUnit}`, c.preview.hoursSavedLabel], [`5–8 ${c.preview.toolsUnit}`, c.preview.toolsLabel]].map(([val, lab]) => (
          <div key={lab} className="px-4 py-3 text-center border-r last:border-r-0 border-white/10">
            <div className="text-base font-bold text-emerald-400">{val}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{lab}</div>
          </div>
        ))}
      </div>
      <div className="p-4 space-y-2.5">
        <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mb-2.5">{c.preview.topToolsEyebrow}</div>
        {[
          { name: "RentSpree", badge: c.preview.toolBadges[0], score: 93, color: "bg-emerald-500" },
          { name: "AppFolio + AI", badge: c.preview.toolBadges[1], score: 89, color: "bg-blue-500" },
          { name: "TurboTenant", badge: c.preview.toolBadges[2], score: 86, color: "bg-cyan-500" },
        ].map((tool) => (
          <div key={tool.name} className="bg-slate-900/80 rounded-xl p-3 border border-white/10/40">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-semibold text-white">{tool.name}</span>
              <span className="text-[10px] text-slate-400">{tool.badge}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div className={`h-full ${tool.color} rounded-full`} initial={{ width: 0 }} animate={{ width: `${tool.score}%` }} transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }} />
              </div>
              <span className="text-[10px] text-slate-500 flex-shrink-0">{tool.score}% {c.preview.matchShort}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="px-4 pb-4">
        <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mb-2.5">{c.preview.roadmapEyebrow}</div>
        <div className="flex gap-2">
          {c.preview.phaseLabels.map((p, i) => (
            <div key={p} className={`flex-1 text-center rounded-lg py-2 px-1 text-[10px] font-medium border ${i === 0 ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400" : "bg-white/8 border-white/10 text-slate-500"}`}>
              {p.split("\n").map((line, j) => <div key={j}>{line}</div>)}
            </div>
          ))}
        </div>
      </div>
    </div>
    <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} className="absolute -top-4 -right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-xl shadow-lg shadow-emerald-500/40">
      {c.preview.floatingBadge1}
    </motion.div>
    <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }} className="absolute -bottom-3 -left-4 bg-teal-600 text-white text-xs font-bold px-3 py-1.5 rounded-xl shadow-lg shadow-teal-600/40">
      {c.preview.floatingBadge2}
    </motion.div>
  </div>
);

const PropertyManagementPage = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  // The page only ships English copy on launch — Spanish/Turkish coming
  // once English funnel proves out. Falls back to English when the
  // user's language isn't in CONTENT.
  const c = CONTENT[lang] || CONTENT.en;
  const [toolCount, setToolCount] = useState(null);
  const displayCount = toolCount || 372;
  const [currentPrice, setCurrentPrice] = useState(149);

  useEffect(() => {
    // R31.7e: capture gclid + utm_* + landing_page on landing-page mount
    // so paid-traffic visitors who land on /pm and bounce around still
    // ship their attribution when they finally submit the audit.
    captureAttribution();
    const base = process.env.REACT_APP_BACKEND_URL || 'https://ai-workshop-llc-backend.onrender.com';
    fetch(`${base}/api/tools/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.total) setToolCount(d.total); })
      .catch(() => {});
    fetch(`${base}/api/pricing/current`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.price) setCurrentPrice(d.price); })
      .catch(() => {});
  }, []);

  // Route to the audit form with industry=real_estate AND
  // sub_vertical=property_management. The audit form reads
  // ?sub_vertical from the URL and stamps it onto the audit submission
  // so the engine's chip-resolver, cluster taxonomy, and FCRA fence
  // see the PM context from the very first hop.
  const goToAudit = () => navigate("/audit?industry=real_estate&sub_vertical=property_management");

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <SEOHead
        path="/property-management"
        lang={lang}
        title={c.title}
        description={c.metaDesc}
        faqItems={c.faq}
        industry="Property Management"
      />
      <Header />

      {/* HERO */}
      <section className="relative pt-20 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-emerald-600/10 rounded-full filter blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-teal-600/10 rounded-full filter blur-[120px]" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        </div>
        <div className="max-w-7xl mx-auto relative">
          {/*
            R31.7e (paid-traffic launch pre-flight): hero block paints
            immediately. Previous version used Framer Motion staggered
            fade-ins (initial opacity 0, delays 0.1-0.5s) which left the
            hero h1+subtitle dim for ~500ms on first paint — exactly
            when paid visitors form the bounce decision. Hero text is
            now statically rendered; motion stagger reserved for
            below-the-fold sections where the visitor has actually
            scrolled in. Preview card on the right keeps its slide-in
            since it's secondary content.
          */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm font-semibold px-4 py-2 rounded-full mb-8">
                <span className="text-base">🏘️</span> {c.heroBadge}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6">
                <span className="text-white">{c.heroTitlePart1}</span>
                <br />
                <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">{c.heroTitlePart2}</span>
              </h1>
              <p className="text-lg text-slate-400 mb-8 leading-relaxed max-w-xl">
                {c.heroSubtitlePre}{" "}
                <span className="text-white font-semibold">{c.heroSubtitleBold}</span>{" "}
                {c.heroSubtitlePost} {displayCount} {c.heroSubtitleEnd}
              </p>
              <ul className="space-y-3 mb-10">
                {c.heroBullets.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <Button onClick={goToAudit} size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-white font-black text-lg px-10 py-6 rounded-2xl shadow-lg shadow-emerald-500/30 transition-all hover:scale-[1.02] active:scale-[0.98]">
                  {c.heroCta} — ${currentPrice} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-xs text-slate-500 self-center">{c.heroCtaFine}</p>
              </div>
            </div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35, duration: 0.7 }} className="hidden lg:block">
              <PMBlueprintPreview c={c} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-6xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-emerald-400 font-semibold tracking-widest uppercase mb-3">{c.painEyebrow}</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">{c.painHeading}</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">{c.painSub}</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {c.painPoints.map((pp, idx) => {
                const Icon = PAIN_ICONS[idx];
                const cls = PAIN_COLORS[idx];
                return (
                  <motion.div key={pp.title} variants={fadeUp} className={`rounded-2xl border p-6 ${cls.bg} ${cls.border}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cls.bg} border ${cls.border} mb-4`}>
                      <Icon className={`w-5 h-5 ${cls.color}`} />
                    </div>
                    <h3 className="text-white font-bold text-base mb-2">{pp.title}</h3>
                    <p className="text-slate-400 text-sm mb-3 leading-relaxed">{pp.desc}</p>
                    <div className={`text-xs font-bold ${cls.color}`}>{pp.stat}</div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TOOL PREVIEW */}
      <section className="py-20 px-4 border-t border-white/8 bg-slate-900/30">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-teal-400 font-semibold tracking-widest uppercase mb-3">{c.toolEyebrow}</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">{c.toolHeading}</h2>
              <p className="text-slate-400 max-w-xl mx-auto">{c.toolSubPre} {displayCount}{c.toolSubPost}</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {c.toolStack.map((tool, idx) => {
                const meta = TOOL_META[idx];
                return (
                  <motion.div key={tool.name} variants={fadeUp} className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-white font-bold text-lg">{tool.name}</div>
                        <div className="text-slate-500 text-xs mt-0.5">{tool.category} — {tool.price}</div>
                      </div>
                      <div className="flex items-center gap-1.5 bg-slate-900/60 border border-white/10 rounded-full px-3 py-1">
                        <div className={`w-2 h-2 rounded-full ${meta.color}`} />
                        <span className="text-xs text-slate-400 font-semibold">{meta.score}% {c.matchLabel}</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm leading-relaxed">{tool.why}</p>
                    <div className="mt-4 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div className={`h-full ${meta.color} rounded-full`} initial={{ width: 0 }} whileInView={{ width: `${meta.score}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3, ease: "easeOut" }} />
                    </div>
                  </motion.div>
                );
              })}
            </div>
            <motion.div variants={fadeUp} className="text-center mt-10">
              <p className="text-slate-500 text-sm">{c.toolFootnotePre} {displayCount}{c.toolFootnotePost}</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-cyan-400 font-semibold tracking-widest uppercase mb-3">{c.getEyebrow}</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">{c.getHeadingPre}{currentPrice}{c.getHeadingPost}</h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {c.whatYouGet.map((item) => (
                <motion.div key={item.title} variants={fadeUp} className="flex gap-4 p-5 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl">
                  <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm mb-1">{item.title}</div>
                    <div className="text-slate-400 text-sm leading-relaxed">{item.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-16 px-4 border-t border-white/8 bg-slate-900/30">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-slate-500 text-sm py-8">
            <span className="text-slate-600">{c.testimonialGuarantee}</span>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 border-t border-white/8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-sm text-emerald-400 font-semibold tracking-widest uppercase mb-3">{c.faqEyebrow}</div>
            <h2 className="text-3xl sm:text-4xl font-black text-white">{c.faqHeading}</h2>
          </div>
          <div className="space-y-3">
            {c.faq.map((item, i) => (
              <details key={i} className="group bg-slate-900/70 border border-white/10 rounded-2xl px-5 py-4 transition-colors hover:border-emerald-500/30">
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                  <span className="text-white font-semibold text-sm leading-snug">{item.q}</span>
                  <span className="text-slate-500 group-open:rotate-45 transition-transform text-xl flex-shrink-0">+</span>
                </summary>
                <p className="text-slate-400 text-sm leading-relaxed mt-3 pt-3 border-t border-white/8">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-24 px-4 border-t border-white/8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-8">
              <Building2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
              {c.bottomHeading1}
              <br />
              <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">{c.bottomHeading2}</span>
            </h2>
            <p className="text-slate-400 text-lg mb-10 leading-relaxed">{c.bottomSub}</p>
            <Button onClick={goToAudit} size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-white font-black text-xl px-12 py-7 rounded-2xl shadow-xl shadow-emerald-500/30 transition-all hover:scale-[1.02] active:scale-[0.98]">
              {c.bottomCta} — ${currentPrice} <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
            <div className="flex items-center justify-center gap-6 mt-8">
              {[{ icon: Clock, text: c.bottomFineReady }, { icon: DollarSign, text: `${c.bottomFineOneTime}${currentPrice}` }, { icon: CheckCircle, text: c.bottomFineNoSub }].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-slate-500 text-sm"><Icon className="w-4 h-4" /><span>{text}</span></div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PropertyManagementPage;
